import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type TopLayerChangedEvent = CustomEvent<{
  action: "add" | "remove";
  target: Element | null;
}>;

type TopLayerProps = {
  children: React.ReactNode;
};

export const PORTAL_CONTAINER_ID = "stotles-top-level-container"; // Unique ID for DOM element + portal ID

// Reason for this wrapper is some components e.g. Sonner doesn't currently support top-layer elements meaning
// toasts can be hidden behind dialogs/popovers. This wrapper moves the container
// to the top-most dialog/popover when one is opened, and back to the body when all are closed.

// I really wish this wasn't necessary but it currently is. Look at https://github.com/whatwg/html/issues/9936
// to see if this problem has been solved properly in the future, but that will likely require a specification change.
const TopLevelWrapper = ({ children }: TopLayerProps) => {
  // Adapted from https://github.com/whatwg/html/issues/9936#issuecomment-2682028665 which was based on https://github.com/whatwg/html/issues/9075#issuecomment-2663527036
  // and I have updated it to work with React

  // For clarity, this container should NOT re-render unless absolutely necessary, since doing so loses the currently displayed toasts
  // Therefore, this manipulates the DOM directly outside of React so it should only render twice
  // (once on mount which creates the default container, second when this created container is marked ready)
  const containerRef = useRef<HTMLElement | null>(null);
  const [containerReady, setContainerReady] = useState(false);

  useEffect(() => {
    // Setup on mount - creates the portal container element if it doesn't exist (which it shouldn't on first render)
    // This element is not managed by React so moving it around won't cause re-renders
    let container = document.getElementById(PORTAL_CONTAINER_ID);
    if (!container) {
      container = document.createElement("div");
      container.id = PORTAL_CONTAINER_ID;
      // Establish a high stacking context so portaled content (tooltips, toasts)
      // renders above fixed elements like the side nav (z-index: 6).
      container.style.position = "relative";
      container.style.zIndex = "9999";
      document.body.appendChild(container);
    }
    containerRef.current = container;
    setContainerReady(true);

    // Store a set of top-layer elements so we can track them and know which is the top-most
    const topLayerElements = new Set<Element>();
    const getTopMostTopLayerElement = () => {
      const topLayers = Array.from(topLayerElements).filter(
        (el) => el instanceof HTMLElement,
      ) as HTMLElement[];
      // Not certain this will always be correct but from testing it seems to be fine under normal usage
      return topLayers.at(-1) || null; // Return the last element which should be the top-most
    };

    // Listen for toggle events which should be fired when a dialog or popover is toggled as top-layer
    const toggleListener = ({ target }: Event) => {
      if (!(target && target instanceof Element)) return;
      if (!(target instanceof HTMLDialogElement || target.hasAttribute("popover"))) {
        // Potentially add logging here in case another component gains the ability to make itself top-level.
        // Exclude <details> if you do, since that triggers this event too but isn't relevant
        return;
      }
      // Custom elements, such as browser extension popovers (e.g. 1Password's save prompt), may
      // render through a shadow root without a slot, so the container would be invisible inside them.
      if (target.tagName.includes("-") || target.shadowRoot) return;

      if (target.matches(":modal, :popover-open") && document.contains(target)) {
        topLayerElements.add(target);
        document.dispatchEvent(
          new CustomEvent("topLayerChanged", {
            detail: { action: "add", target },
          }) as TopLayerChangedEvent,
        );
      } else {
        // Only send event if the element was already tracked, as otherwise it doesn't matter
        if (topLayerElements.delete(target)) {
          document.dispatchEvent(
            new CustomEvent("topLayerChanged", {
              detail: { action: "remove", target },
            }) as TopLayerChangedEvent,
          );
        }
      }
    };
    document.addEventListener("toggle", toggleListener, { capture: true });

    // Detect when elements tracked as top-layer are removed from the DOM so we can move the container
    const observer = new MutationObserver((mutations) => {
      const nodes = mutations.flatMap(({ removedNodes }) => Array.from(removedNodes));
      for (const node of nodes) {
        if (node instanceof Element && topLayerElements.delete(node)) {
          document.dispatchEvent(
            new CustomEvent("topLayerChanged", {
              detail: { action: "remove", target: node },
            }) as TopLayerChangedEvent,
          );
        }
      }
      if (nodes.length > 0) {
        // Check if the container is still in the DOM and re-add it needed
        if (
          containerRef.current !== null &&
          document.getElementById(PORTAL_CONTAINER_ID) === null
        ) {
          // We need to figure out which top-layer elements have been removed
          for (const node of topLayerElements) {
            if (document.contains(node)) {
              continue;
            }
            // Run the change event at the end once for performance
            topLayerElements.delete(node);
          }

          document.dispatchEvent(
            new CustomEvent("topLayerChanged", {
              detail: { action: "remove", target: null },
            }) as TopLayerChangedEvent,
          );
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Handler to move the top layer container in the DOM around
    const handleTopLayerChanged = (e: TopLayerChangedEvent) => {
      const target = e.detail && e.detail.target;
      const action = e.detail && e.detail.action;
      if (
        action === "add" &&
        target &&
        target instanceof HTMLElement &&
        (target.nodeName.toLowerCase() === "dialog" || target.hasAttribute("popover"))
      ) {
        // Always append the container so it should hopefully be the last child on it.
        // Think this should be fine but might need to make the first child due to some edgecases
        // I've not encountered yet as it was in the original code I adapted this from.
        // appendChild also moves the element if it already exists in the DOM, which is great for this scenario

        // TODO: switch to moveBefore since that will do animations and transitions correctly
        // not done yet due to types + minimal browser support & bugs in implementation + still needing this fallback
        target.appendChild(container);
      } else if (action === "remove") {
        // On remove, check for the next top-most dialog or popover
        const nextTopLayer = getTopMostTopLayerElement();
        if (nextTopLayer) {
          nextTopLayer.appendChild(container);
        } else {
          // Move the container back to the body if no top-layer elements are present
          document.body.appendChild(container);
        }
      }
    };
    const handleTopLayerAbortController = new AbortController();
    document.addEventListener("topLayerChanged", handleTopLayerChanged as EventListener, {
      signal: handleTopLayerAbortController.signal,
    });

    return () => {
      handleTopLayerAbortController.abort(); // Stop trying to move the container around midway through if needed
      document.removeEventListener("topLayerChanged", handleTopLayerChanged as EventListener);
      document.removeEventListener("toggle", toggleListener, { capture: true });
      observer.disconnect();
    };
  }, []);

  if (!containerReady || !containerRef.current) return null;

  return createPortal(
    children,
    containerRef.current,
    PORTAL_CONTAINER_ID, // Think this is unnecessary since this isn't re-rendered but have it just in case
  );
};

export default TopLevelWrapper;
